//#region src/error.ts
const NETWORK_ERROR_PATTERNS = [
	"abort",
	"network request failed",
	"load failed",
	"failed to check for updates",
	"failed to fetch update",
	"networkerror",
	"network error",
	"the internet connection appears to be offline",
	"connect econnrefused",
	"net::err_",
	"the network connection was lost",
	"the request was canceled",
	"a server with the specified hostname could not be found"
];
const SENTRY_SUPPRESSION_REASONS = {
	networkFailure: "network_failure",
	deviceStorage: "device_storage",
	versionSkew: "version_skew",
	rateLimit: "rate_limit"
};
const SUPPRESSED_REASON_VALUES = new Set(Object.values(SENTRY_SUPPRESSION_REASONS));
function isSentrySuppressedReason(reason) {
	return typeof reason === "string" && SUPPRESSED_REASON_VALUES.has(reason);
}
function extractErrorText(error, maxDepth = 5) {
	const parts = [];
	let current = error;
	let depth = 0;
	while (depth < maxDepth && current !== void 0 && current !== null) {
		if (current instanceof Error) {
			if (current.message) parts.push(current.message);
			current = current.cause;
		} else {
			parts.push(String(current));
			break;
		}
		depth++;
	}
	return parts.join(" ");
}
function isErrorLike(value) {
	return typeof value === "object" && value !== null && ("message" in value || "name" in value);
}
/**
* Flatten an unknown error into a serializable shape for structured logging:
* name, constructor, message, whether it wraps a cause, and the joined `.cause`
* chain. Surfacing these (as Sentry tags) lets us aggregate on the underlying
* error instead of a generic outer message. Non-`Error` but error-like objects
* (bridged / deserialized errors common in RN) are read duck-typed so we don't
* lose the signal on exactly the shapes this instrumentation exists to reveal.
*/
function describeError(error) {
	if (error instanceof Error) return {
		name: error.name || null,
		constructorName: error.constructor?.name ?? null,
		message: error.message,
		hasCause: "cause" in error && error.cause !== void 0 && error.cause !== null,
		causeChain: extractErrorText(error)
	};
	if (isErrorLike(error)) {
		const message = typeof error.message === "string" ? error.message : error.message !== void 0 && error.message !== null ? String(error.message) : String(error);
		const hasCause = error.cause !== void 0 && error.cause !== null;
		return {
			name: typeof error.name === "string" ? error.name : null,
			constructorName: null,
			message,
			hasCause,
			causeChain: hasCause ? `${message} ${extractErrorText(error.cause)}` : message
		};
	}
	return {
		name: null,
		constructorName: null,
		message: String(error),
		hasCause: false,
		causeChain: String(error)
	};
}
function errorMatchesAnyPattern(error, patterns) {
	const text = extractErrorText(error).toLowerCase();
	return patterns.some((p) => text.includes(p));
}
const AUTH_ERROR_PATTERNS = [
	"invalid token",
	"refresh token",
	"no access token",
	"invalid_grant",
	"invalid_token"
];
function isBrowserFetchTransportFailure(message) {
	const trimmed = message.trim();
	return /^failed to fetch$/i.test(trimmed) || /^typeerror:\s*failed to fetch$/i.test(trimmed) || /^error:\s*failed to fetch$/i.test(trimmed);
}
function isSuppressibleNetworkMessage(message) {
	const lower = message.trim().toLowerCase();
	return isBrowserFetchTransportFailure(lower) || NETWORK_ERROR_PATTERNS.some((pattern) => lower.includes(pattern));
}
function isSuppressibleAuthMessage(message) {
	const lower = message.toLowerCase();
	return AUTH_ERROR_PATTERNS.some((pattern) => lower.includes(pattern));
}
const DISK_FULL_MESSAGE_PATTERNS = [
	"enospc",
	"no space left on device",
	"database or disk is full",
	"disk full"
];
function isSuppressibleDeviceStorageMessage(message) {
	const lower = message.toLowerCase();
	return DISK_FULL_MESSAGE_PATTERNS.some((pattern) => lower.includes(pattern));
}
function isNetworkError(error) {
	if (typeof DOMException !== "undefined" && error instanceof DOMException) return error.name === "AbortError" || error.name === "NetworkError" || isSuppressibleNetworkMessage(error.message);
	if (error instanceof TypeError) return isSuppressibleNetworkMessage(error.message);
	return isSuppressibleNetworkMessage(String(error));
}
/**
* Check if an error is an authentication-related error.
* Works with Error objects, Response objects, and unknown types.
*
* @param error - The error to check
* @returns True if the error appears to be auth-related
*/
function isAuthError(error) {
	if (!error) return false;
	if (isNetworkError(error)) return false;
	if (error instanceof Response) return error.status === 401 || error.status === 403;
	const err = error;
	const status = err?.status ?? err?.response?.status;
	if (status === 401 || status === 403) return true;
	if (isSuppressibleAuthMessage(err?.message ?? String(error ?? ""))) return true;
	if (err?.name === "AuthError") return true;
	const errorCode = err?.error;
	if (errorCode === "invalid_grant" || errorCode === "invalid_token") return true;
	return false;
}
/**
* Custom error class for network errors.
* Can be used across platforms for consistent error handling.
*/
var NetworkError = class extends Error {
	title = "Connection Problem";
	constructor(message = "We're having trouble connecting. Please check your internet connection and try again.") {
		super(message);
		this.name = "NetworkError";
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
/**
* Custom error class for authentication errors.
* Can be used across platforms for consistent error handling.
*/
var AuthError = class extends Error {
	status;
	response;
	constructor(message, status, response) {
		super(message);
		this.name = "AuthError";
		this.status = status;
		this.response = response;
	}
};
var SuppressedError = class extends Error {
	constructor(message, cause) {
		super(message);
		if (cause !== void 0) this.cause = cause;
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
//#endregion
export { AuthError, DISK_FULL_MESSAGE_PATTERNS, NetworkError, SENTRY_SUPPRESSION_REASONS, SuppressedError, describeError, errorMatchesAnyPattern, extractErrorText, isAuthError, isNetworkError, isSentrySuppressedReason, isSuppressibleAuthMessage, isSuppressibleDeviceStorageMessage, isSuppressibleNetworkMessage };

//# sourceMappingURL=error.js.map